/**
 * Framework-free line breaking and glyph placement for the native (WebGL) Control text engine: a
 * port of Godot's Label shaping against a `FontMetrics`, whose px quantisation is `fontMetrics.ts`.
 * Only an `'atlas'` font gets atlas bitmaps. A `'canvas'` scene font gets `glyph: null` for
 * `TextRun.tsx` to rasterise, since an atlas lookup would give its advances Open Sans ink.
 *
 * Portions ported from Godot Engine (MIT).
 * Copyright (c) 2014-present Godot Engine contributors.
 * Copyright (c) 2007-2014 Juan Linietsky, Ariel Manzur.
 * See THIRD-PARTY-NOTICES.md.
 */
import { OPEN_SANS_ATLAS_GLYPHS } from './openSansAtlas';
import { round as godotRound } from '../../../../godot/math/math';
import { hexCodeBoxAdvanceSize } from './hexCodeBox';
import { fontUsesSubpixelPositioning, getFontAscentPx, getFontEmboldenGrowthPx, getFontExtraAdvancePx, getFontGlyphAdvancePx, getFontKerningAdjustmentPx, getFontLinePitchPx, } from './fontMetrics';
import { OPEN_SANS_FONT_METRICS } from './openSansFontMetrics';
import { tabAlignAdvances } from './textTabStops';
import { AutowrapMode } from '../../../../godot/text/textServerEnums';
/**
 * Narrows a parsed `autowrap_mode` to the enum, else returns `fallback`, as leniently as
 * `parseOptionalInt`. The fallback is the caller's: `Label` uses `AUTOWRAP_OFF`, `RichTextLabel` uses
 * `AUTOWRAP_WORD_SMART` (`rich_text_label.h:557`).
 */
export function clampAutowrapMode(mode, fallback) {
    switch (mode) {
        case AutowrapMode.AUTOWRAP_OFF:
        case AutowrapMode.AUTOWRAP_ARBITRARY:
        case AutowrapMode.AUTOWRAP_WORD:
        case AutowrapMode.AUTOWRAP_WORD_SMART:
            return mode;
        default:
            return fallback;
    }
}
/** `TextServer::LineBreakFlag` subset this module reads (`servers/text/text_server.h:113-120`). */
const BREAK_TRIM_START_EDGE_SPACES = 1 << 6;
const BREAK_TRIM_END_EDGE_SPACES = 1 << 7;
/**
 * `_shaped_text_get_size` reports `Size2(sd->width, ascent + descent).ceil()`
 * (`modules/text_server_adv/text_server_adv.cpp:7524-7537`), and `shaped_text_get_width` ceils the
 * same width (`:7561-7570`). The ceil runs toward positive infinity, as `Vector2::ceil()` does.
 */
export function shapedTextSizeWidthPx(widthPx) {
    // `GridContainer` truncates each minimum into `Size2i col_minw` (`grid_container.cpp:290`), so a
    // minimum a fraction below the pixel opens every later column one pixel early.
    return Math.ceil(widthPx);
}
/**
 * Re-wraps one shaped line as a one-line result drawn from y = 0; the caller's `<group>` supplies
 * the cumulative Y. It bypasses `shapeText`, so it echoes the parent's `fontMetrics` and baseline.
 * Override `baselineOffsetPx` and `fontMetrics` for a RichTextLabel `[b]`/`[i]` run shaped at its own
 * size, in its own face.
 */
export function soloLineLayout(line, parent, baselineOffsetPx = parent.baselineOffsetPx, fontMetrics = parent.fontMetrics) {
    return {
        lines: [line],
        linePitchPx: parent.linePitchPx,
        widthPx: line.widthPx,
        heightPx: parent.linePitchPx,
        baselineOffsetPx,
        fontMetrics,
    };
}
/**
 * `Label::_shape()`'s mode-to-flags map (`scene/gui/label.cpp` ~209-225), each mode with
 * `BREAK_MANDATORY` too. OFF sets no soft flag, so only hard breaks start a line.
 */
function breakFlagsForAutowrap(mode) {
    switch (mode) {
        case AutowrapMode.AUTOWRAP_ARBITRARY:
            return { wordBound: false, graphemeBound: true, adaptive: false };
        case AutowrapMode.AUTOWRAP_WORD:
            return { wordBound: true, graphemeBound: false, adaptive: false };
        case AutowrapMode.AUTOWRAP_WORD_SMART:
            return { wordBound: true, graphemeBound: false, adaptive: true };
        case AutowrapMode.AUTOWRAP_OFF:
        default:
            return { wordBound: false, graphemeBound: false, adaptive: false };
    }
}
// core/string/char_utils.h :: is_whitespace() / is_linebreak().
/** Shared with `textOverrun.ts` and `textJustify.ts`, which need the line breaker's edge-space predicate. */
export function isWhitespace(cp) {
    return (cp === 0x20 ||
        cp === 0x00a0 ||
        cp === 0x1680 ||
        (cp >= 0x2000 && cp <= 0x200b) ||
        cp === 0x202f ||
        cp === 0x205f ||
        cp === 0x3000 ||
        cp === 0x2028 ||
        cp === 0x2029 ||
        (cp >= 0x0009 && cp <= 0x000d) ||
        cp === 0x0085);
}
function isLinebreak(cp) {
    return (cp >= 0x000a && cp <= 0x000d) || cp === 0x0085 || cp === 0x2028 || cp === 0x2029;
}
/** `GRAPHEME_IS_TAB`: tab (U+0009) or vertical tab (U+000B) (`text_server_adv.cpp:6369-6371`). */
export function isTabChar(ch) {
    return ch === '\t' || ch === '';
}
/** `core/string/char_utils.h::is_control`: C0 controls and DEL through the C1 range, tab and linebreaks included. */
export function isControlChar(cp) {
    return cp <= 0x001f || (cp >= 0x007f && cp <= 0x009f);
}
/** Whether character `i` starts a run: its size or face differs from the character before it. */
function startsNewRun(style, i) {
    return i > 0 && (style.sizes[i] !== style.sizes[i - 1] || style.faces[i] !== style.faces[i - 1]);
}
/**
 * The `hmtx` advance through `getFontGlyphAdvancePx`, the fixed-point chain of FreeType and HarfBuzz.
 * Not `xadvance` from `openSansAtlas.ts`, which is fixed at the 42 px bake size rather than
 * quantised at the shaped size. A character outside the charset advances by `averageAdvanceUnits`,
 * so it leaves a gap rather than collapsing the line.
 */
function glyphAdvancePx(ch, fontSizePx, metrics) {
    return getFontGlyphAdvancePx(metrics, ch, fontSizePx);
}
/**
 * Unicode category Zs, which with U+0009 is ICU's `u_isblank()`, half of the remainder reset at
 * `text_server_adv.cpp:7057`. Not `isWhitespace()`, which adds U+200B (Cf, no width) and U+2028 and
 * U+2029, which reach the reset through `is_linebreak()`.
 */
function isSpaceSeparator(cp) {
    return (cp === 0x0020 ||
        cp === 0x00a0 ||
        cp === 0x1680 ||
        (cp >= 0x2000 && cp <= 0x200a) ||
        cp === 0x202f ||
        cp === 0x205f ||
        cp === 0x3000);
}
/** `metrics.getKerningAdjustmentUnits` scaled to `fontSizePx` with `getFontKerningAdjustmentPx`. */
function kerningAdjustmentPx(a, b, fontSizePx, metrics) {
    return getFontKerningAdjustmentPx(metrics, a, b, fontSizePx);
}
/**
 * One glyph per character, plus the zero-advance whitespace terminator Label appends
 * (`label.cpp:164`). `style` sets the size and face of each character.
 */
function toBreakGlyphs(text, style, preserveControl = false) {
    const { sizes, faces } = style;
    const glyphs = [];
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        const cp = ch.codePointAt(0);
        const hardBreak = isLinebreak(cp);
        // Tab and the linebreaks take their own branches, so this is only the "no font has a glyph"
        // case (`text_server_adv.cpp:6842-6907`).
        const isControl = !hardBreak && !isTabChar(ch) && isControlChar(cp);
        let advance;
        let emboldenGrowthPx = 0;
        let controlCodepoint;
        if (hardBreak || cp === 0x200b) {
            // A break grapheme and U+200B carry no advance in Godot; the baked
            // charset has no entry for either, so the metrics fallback would give
            // them an average-width one that the overflow test then spends on a
            // spurious wrap.
            advance = 0;
        }
        else if (isControl) {
            if (preserveControl) {
                advance = hexCodeBoxAdvanceSize(sizes[i], cp).x;
                controlCodepoint = cp;
            }
            else {
                // Without `preserve_invalid` or `preserve_control`, no `Glyph` is pushed
                // (`text_server_adv.cpp:6844`): zero width.
                advance = 0;
            }
        }
        else {
            const embolden = faces[i].embolden ?? 0;
            advance = glyphAdvancePx(ch, sizes[i], faces[i]) + getFontExtraAdvancePx(embolden, sizes[i]);
            emboldenGrowthPx = getFontEmboldenGrowthPx(embolden, sizes[i]);
        }
        glyphs.push({
            start: i,
            end: i + 1,
            advance,
            isSpace: isWhitespace(cp),
            isHardBreak: hardBreak,
            isTab: isTabChar(ch),
            controlCodepoint,
            emboldenGrowthPx,
        });
    }
    // Kerning folds into the leading advance, skipped across a size boundary, before the whole-pixel
    // round: a GPOS pair is already inside HarfBuzz's `x_advance` when `text_server_adv.cpp:7077`
    // reads it. Where HarfBuzz quantises the pair is not pinned, as the vendored font has no kerning.
    for (let i = 0; i + 1 < text.length; i++) {
        if (startsNewRun(style, i + 1))
            continue;
        glyphs[i].advance += kerningAdjustmentPx(text[i], text[i + 1], sizes[i], faces[i]);
    }
    roundAdvancesToWholePixels(glyphs, text, style);
    glyphs.push({
        start: text.length,
        end: text.length + 1,
        advance: 0,
        isSpace: true,
        isHardBreak: false,
        isTab: false,
        emboldenGrowthPx: 0,
    });
    return glyphs;
}
/**
 * Above the subpixel threshold Godot rounds each advance to a whole pixel and carries the remainder
 * (`text_server_adv.cpp:7079-7084`; `keep_rounding_remainders` defaults to true,
 * `text_server_adv.h:344,618`), so each prefix sum stays within half a pixel. Mutates `glyphs`.
 */
function roundAdvancesToWholePixels(glyphs, text, style) {
    let advanceRemainder = 0;
    for (let i = 0; i < text.length; i++) {
        const sizePx = style.sizes[i];
        // `adv_rem` is local to `_shape_run` (`:7011`), so a new run starts it at zero.
        if (startsNewRun(style, i))
            advanceRemainder = 0;
        if (fontUsesSubpixelPositioning(sizePx))
            continue;
        const cp = text[i].codePointAt(0);
        // Resets on a tab, a `u_isblank()` character or a break (`:7057`), so a word's error stays in it.
        if (cp === 0x0009 || isSpaceSeparator(cp) || isLinebreak(cp))
            advanceRemainder = 0;
        const fullAdvance = advanceRemainder + glyphs[i].advance;
        const rounded = godotRound(fullAdvance);
        glyphs[i].advance = rounded;
        advanceRemainder = fullAdvance - rounded;
    }
}
/**
 * Port of `TextServer::shaped_text_get_line_breaks` (`servers/text/text_server.cpp` ~1024-1209),
 * the scalar-width overload Label calls. Returns `[start, end)` ranges, one per line, trimmed of
 * edge spaces. The trim narrows only the emitted range: the width check still counts the trimmed
 * space. A `width <= 0` disables the overflow check.
 */
function shapedTextGetLineBreaks(glyphs, width, flags, trim, trimIndent = false) {
    const lSize = glyphs.length;
    const rangeEnd = lSize > 0 ? glyphs[lSize - 1].end : 0;
    // BREAK_TRIM_INDENT (`text_server.cpp:1048-1062`): the leading tab/space run,
    // restarted whenever it alone would overrun the box, then capped at 0.6 of it.
    let indent = 0;
    let indentEnd = 0;
    if (trimIndent) {
        for (let i = 0; i < lSize; i++) {
            const g = glyphs[i];
            if (!g.isTab && !g.isSpace)
                break;
            if (indent + g.advance > width)
                indent = 0;
            indent += g.advance;
            indentEnd = g.end;
        }
        indent = Math.min(indent, 0.6 * width);
    }
    // `l_width` (`:1064`): `width` until a row past the indent starts, then the narrowed width. Each
    // emitted line re-evaluates it.
    let lWidth = width;
    const narrowAfterBreak = (i) => {
        if (width > indent && i > indentEnd)
            lWidth = width - indent;
    };
    const lines = [];
    let width_ = 0;
    let lineStart = 0;
    let lastEnd = lineStart;
    let prevSafeBreak = 0;
    let lastSafeBreak = -1;
    let wordCount = 0;
    let trimNext = false;
    const isSpaceOrBreak = (idx) => glyphs[idx].isSpace || glyphs[idx].isHardBreak;
    for (let i = 0; i < lSize; i++) {
        const g = glyphs[i];
        const adv = g.advance;
        // Overflow check: would adding this glyph exceed the line width, with a
        // recorded safe break to fall back to? `width <= 0` disables this whole
        // branch (AUTOWRAP_OFF's unconstrained width), leaving only hard breaks.
        if (lWidth > 0 && width_ + adv > lWidth && lastSafeBreak >= 0) {
            const curSafeBrk = lastSafeBreak;
            // BREAK_TRIM_START_EDGE_SPACES and BREAK_TRIM_END_EDGE_SPACES walk start and end off edge spaces.
            let startPos = prevSafeBreak;
            let endPos = lastSafeBreak;
            while (trim.start && trimNext && startPos < endPos && isSpaceOrBreak(startPos))
                startPos += 1;
            while (trim.end && startPos <= endPos && endPos > 0 && isSpaceOrBreak(endPos))
                endPos -= 1;
            if (lastEnd <= glyphs[startPos].start && glyphs[startPos].start !== glyphs[endPos].end) {
                lines.push([glyphs[startPos].start, glyphs[endPos].end]);
                narrowAfterBreak(i);
                lastEnd = glyphs[endPos].end;
            }
            trimNext = true;
            lineStart = glyphs[curSafeBrk].end;
            prevSafeBreak = curSafeBrk + 1;
            while (prevSafeBreak < lSize && glyphs[prevSafeBreak].end === lineStart)
                prevSafeBreak++;
            i = curSafeBrk;
            lastSafeBreak = -1;
            width_ = 0;
            wordCount = 0;
            continue;
        }
        // BREAK_MANDATORY is always on, whatever the autowrap mode.
        if (g.isHardBreak) {
            const curSafeBrk = i;
            let startPos = prevSafeBreak;
            let endPos = i;
            while (trim.start && trimNext && startPos < endPos && isSpaceOrBreak(startPos))
                startPos += 1;
            while (trim.end && startPos <= endPos && endPos > 0 && isSpaceOrBreak(endPos))
                endPos -= 1;
            if (lastEnd <= glyphs[startPos].start && glyphs[startPos].start !== glyphs[endPos].end) {
                lines.push([glyphs[startPos].start, glyphs[endPos].end]);
                narrowAfterBreak(i);
                lastEnd = glyphs[i].end;
            }
            trimNext = true;
            lineStart = glyphs[curSafeBrk].end;
            prevSafeBreak = curSafeBrk + 1;
            while (prevSafeBreak < lSize && glyphs[prevSafeBreak].end === lineStart)
                prevSafeBreak++;
            lastSafeBreak = -1;
            width_ = 0;
            wordCount = 0;
            continue;
        }
        // `i >= indent_end` (`text_server.cpp:1169`): a soft break inside the
        // leading indent is not a candidate, so the indent never becomes a row.
        if (flags.wordBound && g.isSpace && i >= indentEnd) {
            lastSafeBreak = i;
            wordCount++;
        }
        if (flags.graphemeBound) {
            lastSafeBreak = i;
        }
        if (flags.adaptive && wordCount === 0) {
            lastSafeBreak = i;
        }
        width_ += adv;
    }
    // Tail: whatever remains after the last break becomes the final line.
    if (lSize > 0) {
        const last = lines.length ? lines[lines.length - 1] : null;
        if (!last || (last[1] < rangeEnd && prevSafeBreak < lSize)) {
            const startPos0 = prevSafeBreak < lSize ? prevSafeBreak : lSize - 1;
            let finalStart;
            if (lastEnd <= glyphs[startPos0].start) {
                let startPos = startPos0;
                const endPos = lSize - 1;
                while (trim.start && trimNext && startPos < endPos && isSpaceOrBreak(startPos))
                    startPos += 1;
                finalStart = glyphs[startPos].start;
            }
            else {
                finalStart = lastEnd;
            }
            lines.push([finalStart, rangeEnd]);
        }
    }
    else {
        lines.push([0, 0]);
    }
    return lines;
}
/** `String::chr(0x200B)`, the per-paragraph terminator `Label::_shape` appends (`label.cpp:164`). */
const PARAGRAPH_TERMINATOR = '\u200b';
/**
 * `txt.split(ps)` (`label.cpp:159`) keeps empty entries, so a run of separators is a run of blank
 * lines. With no separator the text is one unterminated paragraph, as Label3D
 * (`label_3d.cpp:485,530`) and `TextParagraph` shape it, where a blank line collapses.
 */
function splitParagraphs(text, separator) {
    if (separator === undefined || separator === '') {
        return [{ text, offset: 0, terminated: false }];
    }
    const out = [];
    let offset = 0;
    for (const part of text.split(separator)) {
        out.push({ text: part, offset, terminated: true });
        offset += part.length + separator.length;
    }
    return out;
}
/** Shapes `text` into lines and per-glyph placements at `options.fontSizePx`, against `options.fontMetrics` (default `OPEN_SANS_FONT_METRICS`). */
export function shapeText(text, options) {
    const { fontSizePx, boxWidthPx, autowrapMode, uppercase = false, lineSpacingPx, fontSizePxAt, fontMetricsAt, fontMetrics = OPEN_SANS_FONT_METRICS, paragraphSeparator, tabStopsPx, autowrapTrimFlags, trimIndent = false, preserveControl = false, } = options;
    const hasTabStops = !!tabStopsPx && tabStopsPx.length > 0;
    // Uppercase before indices are assigned, so a `fontSizePxAt` keyed on the transformed text lines up.
    const transformed = uppercase ? text.toUpperCase() : text;
    const flags = breakFlagsForAutowrap(autowrapMode);
    const trim = {
        start: autowrapTrimFlags === undefined ? true : (autowrapTrimFlags & BREAK_TRIM_START_EDGE_SPACES) !== 0,
        end: autowrapTrimFlags === undefined ? true : (autowrapTrimFlags & BREAK_TRIM_END_EDGE_SPACES) !== 0,
    };
    // AUTOWRAP_OFF never soft-wraps: force an unconstrained width regardless of
    // what the caller passed, so only the always-on hard-break branch can
    // start a new line (mirrors Label::_shape() never OR-ing a wrap flag in).
    const effectiveWidth = autowrapMode === AutowrapMode.AUTOWRAP_OFF ? 0 : boxWidthPx;
    const linePitchPx = getFontLinePitchPx(fontMetrics, fontSizePx, lineSpacingPx);
    const lines = [];
    for (const para of splitParagraphs(transformed, paragraphSeparator)) {
        // The terminator is in the shaped text but not the source, so the per-character closures, keyed
        // into the whole text, are offset back, and the index past the paragraph's end takes the last value.
        const sourceIndex = (i) => para.offset + Math.max(0, Math.min(i, para.text.length - 1));
        const paraText = para.terminated ? para.text + PARAGRAPH_TERMINATOR : para.text;
        const runStyle = {
            sizes: Array.from({ length: paraText.length }, (_v, i) => fontSizePxAt ? fontSizePxAt(sourceIndex(i)) : fontSizePx),
            faces: Array.from({ length: paraText.length }, (_v, i) => fontMetricsAt ? fontMetricsAt(sourceIndex(i)) : fontMetrics),
        };
        const breakGlyphs = toBreakGlyphs(paraText, runStyle, preserveControl);
        // First tab pass: the aligned advance must be in place before line breaking measures it.
        if (hasTabStops) {
            const paragraphEntries = Array.from(paraText, (char, i) => ({
                char,
                advance: breakGlyphs[i].advance,
            }));
            const aligned = tabAlignAdvances(paragraphEntries, tabStopsPx);
            for (let i = 0; i < aligned.length; i++)
                breakGlyphs[i].advance = aligned[i];
        }
        for (const [start, end] of shapedTextGetLineBreaks(breakGlyphs, effectiveWidth, flags, trim, trimIndent)) {
            const clampedEnd = Math.min(end, paraText.length);
            // Second tab pass: restarts the tab-stop cycle at this line's pen origin.
            const lineAdvances = hasTabStops
                ? tabAlignAdvances(Array.from({ length: clampedEnd - start }, (_v, i) => ({
                    char: paraText[start + i],
                    advance: breakGlyphs[start + i].advance,
                })), tabStopsPx)
                : null;
            let penX = 0;
            const glyphs = [];
            for (let idx = start; idx < clampedEnd; idx++) {
                const ch = paraText[idx];
                const advance = lineAdvances ? lineAdvances[idx - start] : breakGlyphs[idx].advance;
                glyphs.push({
                    char: ch,
                    x: penX,
                    advance,
                    // Atlas bitmaps exist only for the font `openSansAtlas.ts` bakes.
                    glyph: runStyle.faces[idx].kind === 'atlas' ? (OPEN_SANS_ATLAS_GLYPHS[ch] ?? null) : null,
                    controlCodepoint: breakGlyphs[idx].controlCodepoint,
                    emboldenGrowthPx: breakGlyphs[idx].emboldenGrowthPx || undefined,
                    fontMetrics: runStyle.faces[idx],
                });
                penX += advance;
            }
            lines.push({ text: paraText.slice(start, clampedEnd), glyphs, widthPx: penX });
        }
    }
    const widthPx = lines.reduce((max, l) => Math.max(max, l.widthPx), 0);
    const heightPx = lines.length * linePitchPx;
    const baselineOffsetPx = getFontAscentPx(fontMetrics, fontSizePx);
    return { lines, linePitchPx, widthPx, heightPx, baselineOffsetPx, fontMetrics };
}
//# sourceMappingURL=textLayout.js.map